enum EventType {
  ElementClicked = "redventures.usertracking.v3.ElementClicked",
  ApplicationClicked = "redventures.usertracking.v3.ApplicationClicked",
  ElementViewed = "redventures.usertracking.v3.ElementViewed",
  FieldInputted = "redventures.usertracking.v3.FieldInputted",
  FormErrored = "redventures.usertracking.v3.FormErrored",
  FormSubmitted = "redventures.usertracking.v3.FormSubmitted",
  WebVitals = "core.WebVitals.v1",
  TempMonarchDecision = "core.ElementLoaded.v1",
  ElementLoaded = "core.ElementLoaded.v1",
  ThankYouPageDecision = "2u.ThankYouPageDecision.v1",
  ProductClicked = "redventures.ecommerce.v1.ProductClicked",
  ProductViewed = "redventures.ecommerce.v1.ProductViewed",
  ProductLoaded = "redventures.ecommerce.v1.ProductLoaded",
  DAPICorrelation = "2u.dapi.ConsentCorrelation.v2",
}

interface WebElement {
  elementType?: string;
  htmlId?: string;
  location?: string;
  name?: string;
  position?: string;
  text?: string;
}

interface FormContext {
  formBrand?: string;
  formId?: string;
  formName?: string;
  formType?: string;
  formVersion?: string;
}
interface StepContext {
  stepId?: string;
  stepName?: string;
  stepNumber?: number | "TY Page";
  stepType?:
    | "UNKNOWN"
    | "SERVICEABILITY"
    | "CONTACT"
    | "PRODUCT"
    | "CONFIRMATION"
    | "CREDENTIALS"
    | "PREFERENCE"
    | "AUTHENTICATION";
}

interface Field {
  autofilled?: boolean;
  fieldId?: string;
  fieldLabel?: string;
  fieldName?: string;
  fieldType?: string;
  fieldValue?: string;
  fieldSource?: string;
  fieldValueSensitive?: string;
  fieldValueRestricted?: string;
  fieldNumber?: number;
}

interface ElementClicked {
  webElement?: WebElement;
}

interface ApplicationClicked {
  webElement?: WebElement;
}

interface ElementViewed {
  webElement?: WebElement;
}

interface FieldInputted {
  formContext?: FormContext;
  stepContext?: StepContext;
  userInputField?: Field;
}

interface FormErrored {
  errorMessage: string;
  formContext?: FormContext;
  stepContext?: StepContext;
}

interface FormSubmitted {
  field?: Omit<Field, "fieldNumber">[];
  formContext?: FormContext;
  outboundUrl?: string;
  stepContext?: StepContext;
}

interface WebVitals {
  metricName: string;
  value: number;
  rating: "GOOD" | "POOR" | "NI";
}

interface ElementLoaded {
  webElement?: WebElement;
}

interface ThankYouPageDecision {
  redirectCookie: string;
}

interface ProductEvent {
  product: {
    productId: string;
    name: string;
    category: string;
    brand: string;
    variant: string;
    position?: number;
  },
  viewCorrelationId: string;
}

interface DAPICorrelation {
  dapiRandomId: string; // Generated during preconsent
}

class EventClient {
  beam<T>(type: EventType, data: T) {
    if (window.tagular)
      window.tagular("beam", {
        "@type": type,
        ...data,
      });
  }

  elementClicked(data: ElementClicked) {
    this.beam<ElementClicked>(EventType.ElementClicked, data);
  }

  applicationClicked(data: ApplicationClicked) {
    this.beam<ApplicationClicked>(EventType.ApplicationClicked, data);
  }

  elementViewed(data: ElementViewed) {
    this.beam<ElementViewed>(EventType.ElementViewed, data);
  }
  fieldInputted(data: FieldInputted) {
    this.beam<FieldInputted>(EventType.FieldInputted, data);
  }

  formErrored(data: FormErrored) {
    this.beam(EventType.FormErrored, data);
  }

  formSubmitted(data: FormSubmitted) {
    this.beam(EventType.FormSubmitted, data);
  }

  webVitals(data: WebVitals) {
    this.beam<WebVitals>(EventType.WebVitals, data);
  }

  tempMonarchDecision(data: ElementLoaded) {
    this.beam<ElementLoaded>(EventType.TempMonarchDecision, data);
  }

  elementLoaded(data: ElementLoaded) {
    this.beam<ElementLoaded>(EventType.ElementLoaded, data);
  }

  thankYouPageDecision(data: ThankYouPageDecision) {
    this.beam<ThankYouPageDecision>(EventType.ThankYouPageDecision, data);
  }

  productClicked(data: ProductEvent) {
    this.beam<ProductEvent>(EventType.ProductClicked, data);
  }

  productViewed(data: ProductEvent) {
    this.beam<ProductEvent>(EventType.ProductViewed, data);
  }

  productLoaded(data: ProductEvent) {
    this.beam<ProductEvent>(EventType.ProductLoaded, data);
  }

  dapiCorrelation(data: DAPICorrelation) {
    this.beam<DAPICorrelation>(EventType.DAPICorrelation, data);
  }
}

const eventClient = new EventClient();

export default eventClient;
